import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../services/api';
import { Settings, MapPin, Clock, DollarSign, Save, CheckCircle, XCircle, RefreshCw, Power, Printer, Wifi, Usb, Cable, TestTube, MessageCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import io from 'socket.io-client';
import './Configuracoes.css';

const Configuracoes = () => {
  const [abaAtiva, setAbaAtiva] = useState('geral');
  const queryClient = useQueryClient();

  const { data: estabelecimento, error: estabelecimentoError, isLoading: loadingEstabelecimento } = useQuery(
    'estabelecimento-config',
    async () => {
      try {
        console.log('🔍 Buscando estabelecimento do usuário...');
        const response = await api.get('/api/estabelecimentos/me/estabelecimento');
        const est = response.data.data.estabelecimento;
        console.log('✅ Estabelecimento encontrado:', est?._id);
        console.log('📊 Configurações:', est?.configuracoes);
        console.log('📊 TaxasPorBairro:', est?.configuracoes?.taxasPorBairro?.length || 0, 'bairros');
        return est;
      } catch (error) {
        console.log('⚠️ Erro ao buscar estabelecimento do usuário:', error.response?.status);
        // Se não encontrar estabelecimento do usuário, buscar o padrão
        if (error.response?.status === 404) {
          try {
            console.log('🔍 Buscando estabelecimento padrão...');
            const response = await api.get('/api/estabelecimentos/padrao');
            const est = response.data.data.estabelecimento;
            console.log('✅ Estabelecimento padrão encontrado:', est?._id);
            console.log('📊 Configurações:', est?.configuracoes);
            console.log('📊 TaxasPorBairro:', est?.configuracoes?.taxasPorBairro?.length || 0, 'bairros');
            return est;
          } catch (padraoError) {
            console.error('❌ Erro ao buscar estabelecimento padrão:', padraoError);
            console.error('❌ Resposta completa:', padraoError.response?.data);
            // Retornar null para não quebrar a interface
            return null;
          }
        }
        throw error;
      }
    },
    {
      retry: 1,
      refetchOnWindowFocus: false,
      enabled: true, // Sempre tentar buscar
      staleTime: 0, // Sempre buscar dados frescos
      cacheTime: 0 // Não cachear
    }
  );

  return (
    <div className="configuracoes-page">
      <div className="page-header">
        <div>
          <h2><Settings size={28} /> Configurações</h2>
          <p>Configure seu estabelecimento</p>
        </div>
      </div>

      <div className="config-layout">
        <div className="config-sidebar">
          <button
            className={`config-menu-item ${abaAtiva === 'geral' ? 'ativo' : ''}`}
            onClick={() => setAbaAtiva('geral')}
          >
            <Settings size={20} />
            Geral
          </button>
          <button
            className={`config-menu-item ${abaAtiva === 'taxas' ? 'ativo' : ''}`}
            onClick={() => setAbaAtiva('taxas')}
          >
            <DollarSign size={20} />
            Taxas e Horários
          </button>
          <button
            className={`config-menu-item ${abaAtiva === 'areas' ? 'ativo' : ''}`}
            onClick={() => setAbaAtiva('areas')}
          >
            <MapPin size={20} />
            Áreas de Entrega
          </button>
          <button
            className={`config-menu-item ${abaAtiva === 'horarios' ? 'ativo' : ''}`}
            onClick={() => setAbaAtiva('horarios')}
          >
            <Clock size={20} />
            Horários de Funcionamento
          </button>
          <button
            className={`config-menu-item ${abaAtiva === 'impressora' ? 'ativo' : ''}`}
            onClick={() => setAbaAtiva('impressora')}
          >
            <Printer size={20} />
            Impressora
          </button>
          <button
            className={`config-menu-item ${abaAtiva === 'whatsapp' ? 'ativo' : ''}`}
            onClick={() => setAbaAtiva('whatsapp')}
          >
            <MessageCircle size={20} />
            WhatsApp
          </button>
        </div>

        <div className="config-content">
          {loadingEstabelecimento && (
            <div style={{ 
              padding: '3rem', 
              textAlign: 'center',
              color: '#64748b'
            }}>
              <p>Carregando configurações...</p>
            </div>
          )}
          
          {estabelecimentoError && estabelecimentoError.response?.status === 404 && (
            <div style={{ 
              padding: '2rem', 
              background: '#fff3cd', 
              border: '1px solid #ffc107', 
              borderRadius: '0.5rem',
              marginBottom: '1rem'
            }}>
              <h3 style={{ color: '#856404', marginBottom: '0.5rem' }}>⚠️ Estabelecimento não encontrado</h3>
              <p style={{ color: '#856404', marginBottom: '1rem' }}>
                Execute o script de inicialização para criar o estabelecimento:
              </p>
              <code style={{ 
                display: 'block', 
                padding: '0.75rem', 
                background: '#fff', 
                borderRadius: '0.25rem',
                marginBottom: '1rem'
              }}>
                npm run init
              </code>
            </div>
          )}

          {!loadingEstabelecimento && !estabelecimento && !estabelecimentoError && (
            <div style={{ 
              padding: '2rem', 
              background: '#fee2e2', 
              border: '1px solid #ef4444', 
              borderRadius: '0.5rem',
              marginBottom: '1rem'
            }}>
              <h3 style={{ color: '#991b1b', marginBottom: '0.5rem' }}>❌ Erro ao carregar estabelecimento</h3>
              <p style={{ color: '#991b1b' }}>
                Não foi possível carregar os dados do estabelecimento. Verifique o console para mais detalhes.
              </p>
            </div>
          )}

          {!loadingEstabelecimento && estabelecimento && (
            <>
              {abaAtiva === 'geral' && <ConfigGeral estabelecimento={estabelecimento} />}
              {abaAtiva === 'taxas' && <ConfigTaxas estabelecimento={estabelecimento} />}
              {abaAtiva === 'areas' && <ConfigAreas estabelecimento={estabelecimento} />}
              {abaAtiva === 'horarios' && <ConfigHorarios estabelecimento={estabelecimento} />}
              {abaAtiva === 'impressora' && <ConfigImpressora estabelecimento={estabelecimento} />}
              {abaAtiva === 'whatsapp' && <ConfigWhatsApp estabelecimento={estabelecimento} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ConfigGeral = ({ estabelecimento }) => {
  const [formData, setFormData] = useState({
    nome: estabelecimento?.nome || '',
    contato: {
      telefone: estabelecimento?.contato?.telefone || '',
      whatsapp: estabelecimento?.contato?.whatsapp || '',
      email: estabelecimento?.contato?.email || ''
    },
    endereco: {
      rua: estabelecimento?.endereco?.rua || '',
      numero: estabelecimento?.endereco?.numero || '',
      complemento: estabelecimento?.endereco?.complemento || '',
      bairro: estabelecimento?.endereco?.bairro || '',
      cidade: estabelecimento?.endereco?.cidade || '',
      estado: estabelecimento?.endereco?.estado || '',
      cep: estabelecimento?.endereco?.cep || ''
    },
    descricao: estabelecimento?.descricao || '',
    aceitaPedidos: estabelecimento?.configuracoes?.aceitaPedidos !== false
  });
  const queryClient = useQueryClient();

  const updateContatoField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contato: {
        ...(prev.contato || {}),
        [field]: value
      }
    }));
  };

  const updateEnderecoField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      endereco: {
        ...(prev.endereco || {}),
        [field]: value
      }
    }));
  };

  // Atualizar quando estabelecimento mudar
  useEffect(() => {
    if (!estabelecimento) return;

    setFormData(prev => ({
      ...prev,
      nome: estabelecimento.nome || prev.nome,
      contato: {
        telefone: estabelecimento?.contato?.telefone ?? prev?.contato?.telefone ?? '',
        whatsapp: estabelecimento?.contato?.whatsapp ?? prev?.contato?.whatsapp ?? '',
        email: estabelecimento?.contato?.email ?? prev?.contato?.email ?? ''
      },
      endereco: {
        rua: estabelecimento?.endereco?.rua ?? prev?.endereco?.rua ?? '',
        numero: estabelecimento?.endereco?.numero ?? prev?.endereco?.numero ?? '',
        complemento: estabelecimento?.endereco?.complemento ?? prev?.endereco?.complemento ?? '',
        bairro: estabelecimento?.endereco?.bairro ?? prev?.endereco?.bairro ?? '',
        cidade: estabelecimento?.endereco?.cidade ?? prev?.endereco?.cidade ?? '',
        estado: estabelecimento?.endereco?.estado ?? prev?.endereco?.estado ?? '',
        cep: estabelecimento?.endereco?.cep ?? prev?.endereco?.cep ?? ''
      },
      descricao: estabelecimento.descricao || prev.descricao,
      aceitaPedidos: estabelecimento?.configuracoes?.aceitaPedidos !== false
    }));
  }, [estabelecimento]);

  const saveMutation = useMutation(
    (data) => {
      const configuracoesAtuais = estabelecimento?.configuracoes || {};
      return api.put(`/api/estabelecimentos/${estabelecimento?._id}`, {
        nome: data.nome,
        descricao: data.descricao,
        contato: data.contato,
        endereco: data.endereco,
        configuracoes: {
          ...configuracoesAtuais,
          aceitaPedidos: data.aceitaPedidos
        }
      });
    },
    {
      onSuccess: () => {
        toast.success('Configurações salvas!');
        queryClient.invalidateQueries('estabelecimento-config');
        queryClient.invalidateQueries('estabelecimento-padrao'); // Atualizar no cardápio
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao salvar');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    saveMutation.mutate(formData);
  };

  return (
    <div className="config-section">
      <h3>Configurações Gerais</h3>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Nome do Estabelecimento</label>
          <input
            type="text"
            value={formData.nome}
            onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
            required
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Telefone</label>
            <input
              type="text"
              value={formData.contato?.telefone || ''}
              onChange={(e) => updateContatoField('telefone', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.contato?.email || ''}
              onChange={(e) => updateContatoField('email', e.target.value)}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>WhatsApp</label>
            <input
              type="text"
              value={formData.contato?.whatsapp || ''}
              onChange={(e) => updateContatoField('whatsapp', e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label>Endereço</label>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Rua</label>
            <input
              type="text"
              value={formData.endereco?.rua || ''}
              onChange={(e) => updateEnderecoField('rua', e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Número</label>
            <input
              type="text"
              value={formData.endereco?.numero || ''}
              onChange={(e) => updateEnderecoField('numero', e.target.value)}
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Complemento</label>
            <input
              type="text"
              value={formData.endereco?.complemento || ''}
              onChange={(e) => updateEnderecoField('complemento', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Bairro</label>
            <input
              type="text"
              value={formData.endereco?.bairro || ''}
              onChange={(e) => updateEnderecoField('bairro', e.target.value)}
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Cidade</label>
            <input
              type="text"
              value={formData.endereco?.cidade || ''}
              onChange={(e) => updateEnderecoField('cidade', e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Estado</label>
            <input
              type="text"
              value={formData.endereco?.estado || ''}
              onChange={(e) => updateEnderecoField('estado', e.target.value)}
              maxLength={2}
              required
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>CEP</label>
            <input
              type="text"
              value={formData.endereco?.cep || ''}
              onChange={(e) => updateEnderecoField('cep', e.target.value)}
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label>Descrição</label>
          <textarea
            value={formData.descricao}
            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
            rows={4}
          />
        </div>

        {/* Controle para aceitar pedidos */}
        <div className="form-group" style={{ 
          marginTop: '1.5rem', 
          padding: '1.5rem', 
          background: formData.aceitaPedidos ? '#d4edda' : '#f8d7da', 
          borderRadius: '0.5rem', 
          border: `2px solid ${formData.aceitaPedidos ? '#28a745' : '#dc3545'}` 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.25rem', display: 'block', fontSize: '1.1rem' }}>
                Status de Pedidos Online
              </label>
              <p style={{ margin: 0, fontSize: '0.95rem', color: formData.aceitaPedidos ? '#155724' : '#721c24' }}>
                {formData.aceitaPedidos 
                  ? '✅ Estabelecimento está ACEITANDO pedidos online (site e WhatsApp)' 
                  : '🚫 Estabelecimento NÃO está aceitando pedidos online (site e WhatsApp)'}
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                const novoValor = !formData.aceitaPedidos;
                setFormData({ ...formData, aceitaPedidos: novoValor });
                saveMutation.mutate({ ...formData, aceitaPedidos: novoValor });
              }}
              disabled={saveMutation.isLoading}
              style={{
                padding: '0.75rem 1.5rem',
                background: formData.aceitaPedidos ? '#dc3545' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: saveMutation.isLoading ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                whiteSpace: 'nowrap',
                opacity: saveMutation.isLoading ? 0.7 : 1
              }}
            >
              <Power size={18} />
              {formData.aceitaPedidos ? 'FECHAR' : 'ABRIR'} PEDIDOS
            </button>
          </div>
          <small style={{ display: 'block', marginTop: '0.5rem', color: '#6c757d' }}>
            Use este controle para bloquear novos pedidos quando o estabelecimento fechar mais cedo ou estiver indisponível.
            O status também pode ser alterado na página do Caixa. Vendas presenciais (PDV) não são afetadas.
          </small>
        </div>

        <button type="submit" className="btn btn-primary">
          <Save size={20} />
          Salvar
        </button>
      </form>
    </div>
  );
};

const ConfigTaxas = ({ estabelecimento }) => {
  const [taxaPadrao, setTaxaPadrao] = useState(0);
  const [taxasPorBairro, setTaxasPorBairro] = useState([]);
  const [taxaEntregaAtiva, setTaxaEntregaAtiva] = useState(true);
  const [marmitaHorarioInicio, setMarmitaHorarioInicio] = useState('10:00');
  const [marmitaHorarioFim, setMarmitaHorarioFim] = useState('14:30');
  const [marmitaIntervaloMin, setMarmitaIntervaloMin] = useState(30);
  const [bairroEditando, setBairroEditando] = useState(null);
  const [novoBairro, setNovoBairro] = useState({ bairro: '', taxa: 0 });
  const [busca, setBusca] = useState('');
  const queryClient = useQueryClient();

  // Atualizar quando estabelecimento mudar
  useEffect(() => {
    console.log('🔄 ConfigTaxas - estabelecimento mudou:', estabelecimento);
    console.log('🔄 Estabelecimento completo:', JSON.stringify(estabelecimento, null, 2));
    
    if (!estabelecimento) {
      console.log('⚠️ Estabelecimento é null/undefined');
      setTaxaPadrao(0);
      setTaxasPorBairro([]);
      return;
    }

    if (estabelecimento?.configuracoes) {
      console.log('📊 Dados do estabelecimento:', estabelecimento.configuracoes);
      console.log('📊 TaxasPorBairro:', estabelecimento.configuracoes.taxasPorBairro);
      const taxaPadraoValue = Number(estabelecimento.configuracoes.taxaEntrega) || 0;
      const taxaAtivaValue = estabelecimento?.configuracoes?.taxaEntregaAtiva !== false;
      const marmitaInicioValue = String(estabelecimento?.configuracoes?.marmitaHorarioInicio || '10:00');
      const marmitaFimValue = String(estabelecimento?.configuracoes?.marmitaHorarioFim || '14:30');
      const marmitaIntervaloValue = Number(estabelecimento?.configuracoes?.marmitaIntervaloMin) || 30;
      
      // Garantir que taxasPorBairro seja um array válido
      let taxas = [];
      if (Array.isArray(estabelecimento.configuracoes.taxasPorBairro)) {
        taxas = estabelecimento.configuracoes.taxasPorBairro.filter(t => t && t.bairro && t.bairro.trim()); // Filtrar itens inválidos
      } else if (estabelecimento.configuracoes.taxasPorBairro) {
        // Se não for array, tentar converter
        try {
          taxas = Object.values(estabelecimento.configuracoes.taxasPorBairro).filter(t => t && t.bairro && t.bairro.trim());
        } catch (e) {
          console.error('Erro ao converter taxasPorBairro:', e);
          taxas = [];
        }
      }
      
      console.log('📊 Taxas carregadas:', taxas.length, 'bairros');
      console.log('📊 Primeiros 3 bairros:', taxas.slice(0, 3));
      setTaxaPadrao(taxaPadraoValue);
      setTaxaEntregaAtiva(taxaAtivaValue);
      setMarmitaHorarioInicio(marmitaInicioValue);
      setMarmitaHorarioFim(marmitaFimValue);
      setMarmitaIntervaloMin(marmitaIntervaloValue);
      setTaxasPorBairro(taxas);
    } else if (estabelecimento) {
      console.log('⚠️ Estabelecimento sem configuracoes:', estabelecimento);
      console.log('⚠️ Chaves do estabelecimento:', Object.keys(estabelecimento));
      setTaxaPadrao(0);
      setTaxaEntregaAtiva(true);
      setMarmitaHorarioInicio('10:00');
      setMarmitaHorarioFim('14:30');
      setMarmitaIntervaloMin(30);
      setTaxasPorBairro([]);
    } else {
      console.log('⚠️ Estabelecimento não carregado ainda');
    }
  }, [estabelecimento]);

  const toggleTaxaEntregaMutation = useMutation(
    async (novoValor) => {
      return api.patch(`/api/estabelecimentos/${estabelecimento?._id}/taxa-entrega`, {
        ativa: novoValor
      });
    },
    {
      onSuccess: (_, novoValor) => {
        setTaxaEntregaAtiva(novoValor);
        toast.success(novoValor ? '✅ Taxa de entrega ATIVADA' : '🎁 Entrega grátis (taxa DESATIVADA)');
        queryClient.invalidateQueries('estabelecimento-config');
        queryClient.invalidateQueries('estabelecimento-caixa-fullscreen');
        queryClient.invalidateQueries('estabelecimento-padrao');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao atualizar taxa de entrega');
      }
    }
  );

  const saveMutation = useMutation(
    async (data) => {
      const configuracoesAtuais = estabelecimento?.configuracoes || {};
      return api.put(`/api/estabelecimentos/${estabelecimento?._id}`, {
        configuracoes: {
          ...configuracoesAtuais,
          taxaEntrega: data.taxaPadrao,
          taxasPorBairro: data.taxasPorBairro,
          marmitaHorarioInicio: data.marmitaHorarioInicio,
          marmitaHorarioFim: data.marmitaHorarioFim,
          marmitaIntervaloMin: data.marmitaIntervaloMin
        }
      });
    },
    {
      onSuccess: () => {
        toast.success('Taxas atualizadas com sucesso!');
        queryClient.invalidateQueries('estabelecimento-config');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao salvar taxas');
      }
    }
  );

  const handleSalvarTaxaPadrao = () => {
    if (!estabelecimento?._id) {
      toast.error('Estabelecimento não encontrado');
      return;
    }
    saveMutation.mutate({
      taxaPadrao,
      taxasPorBairro,
      marmitaHorarioInicio,
      marmitaHorarioFim,
      marmitaIntervaloMin
    });
  };

  const handleSalvarTudo = () => {
    if (!estabelecimento?._id) {
      toast.error('Estabelecimento não encontrado');
      return;
    }
    saveMutation.mutate({
      taxaPadrao,
      taxasPorBairro,
      marmitaHorarioInicio,
      marmitaHorarioFim,
      marmitaIntervaloMin
    });
  };

  const adicionarBairro = () => {
    if (!novoBairro.bairro.trim()) {
      toast.error('Digite o nome do bairro');
      return;
    }

    const bairroNormalizado = novoBairro.bairro.trim().toUpperCase();
    
    // Verificar se já existe
    if (taxasPorBairro.some(t => t.bairro.trim().toUpperCase() === bairroNormalizado)) {
      toast.error('Este bairro já está cadastrado');
      return;
    }

    setTaxasPorBairro([
      ...taxasPorBairro,
      {
        bairro: novoBairro.bairro.trim(),
        taxa: parseFloat(novoBairro.taxa) || 0
      }
    ]);
    setNovoBairro({ bairro: '', taxa: 0 });
    toast.success('Bairro adicionado! Clique em "Salvar" para confirmar.');
  };

  const iniciarEdicao = (index) => {
    setBairroEditando(index);
  };

  const salvarEdicao = (index) => {
    const bairroAtualizado = taxasPorBairro[index];
    const bairroNormalizado = bairroAtualizado.bairro.trim().toUpperCase();
    
    // Verificar duplicatas (exceto o próprio item)
    const duplicado = taxasPorBairro.some((t, i) => 
      i !== index && t.bairro.trim().toUpperCase() === bairroNormalizado
    );
    
    if (duplicado) {
      toast.error('Este bairro já está cadastrado');
      return;
    }

    setBairroEditando(null);
    toast.success('Bairro atualizado! Clique em "Salvar" para confirmar.');
  };

  const cancelarEdicao = () => {
    setBairroEditando(null);
    // Recarregar dados originais
    if (estabelecimento?.configuracoes?.taxasPorBairro) {
      setTaxasPorBairro(estabelecimento.configuracoes.taxasPorBairro);
    }
  };

  const removerBairro = (index) => {
    if (window.confirm('Tem certeza que deseja remover este bairro?')) {
      const novasTaxas = taxasPorBairro.filter((_, i) => i !== index);
      setTaxasPorBairro(novasTaxas);
      toast.success('Bairro removido! Clique em "Salvar" para confirmar.');
    }
  };

  const atualizarBairro = (index, campo, valor) => {
    const novasTaxas = [...taxasPorBairro];
    if (campo === 'taxa') {
      novasTaxas[index].taxa = parseFloat(valor) || 0;
    } else {
      novasTaxas[index].bairro = valor;
    }
    setTaxasPorBairro(novasTaxas);
  };

  // Filtrar bairros pela busca
  const bairrosFiltrados = taxasPorBairro.filter(taxa =>
    taxa.bairro.toLowerCase().includes(busca.toLowerCase())
  );

  // Ordenar alfabeticamente
  const bairrosOrdenados = [...bairrosFiltrados].sort((a, b) =>
    a.bairro.localeCompare(b.bairro)
  );

  // Debug: verificar estado atual
  console.log('🎨 ConfigTaxas renderizando - estabelecimento:', estabelecimento?._id);
  console.log('🎨 Taxa padrão:', taxaPadrao);
  console.log('🎨 Taxas por bairro:', taxasPorBairro.length, 'bairros');
  console.log('🎨 Estabelecimento completo:', estabelecimento);
  console.log('🎨 Configurações:', estabelecimento?.configuracoes);
  console.log('🎨 TaxasPorBairro do estabelecimento:', estabelecimento?.configuracoes?.taxasPorBairro);

  if (!estabelecimento) {
    return (
      <div className="config-section">
        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>⏳ Carregando dados do estabelecimento...</p>
          <p style={{ fontSize: '0.875rem', color: '#ef4444' }}>Debug: estabelecimento é null/undefined</p>
        </div>
      </div>
    );
  }

  if (!estabelecimento.configuracoes) {
    return (
      <div className="config-section">
        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>⚠️ Configurações não encontradas</p>
          <p style={{ fontSize: '0.875rem', color: '#ef4444' }}>
            Debug: Estabelecimento ID: {estabelecimento._id} | Configurações: {estabelecimento.configuracoes ? 'Sim' : 'Não'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="config-section">
      <h3>Taxas de Entrega por Bairro</h3>
      <p className="config-description">
        Configure as taxas de entrega para cada bairro. Quando um cliente informar o endereço,
        o sistema calculará automaticamente a taxa baseada no bairro.
      </p>

      {/* Debug info - SEMPRE mostrar para debug */}
      <div style={{ 
        padding: '1rem', 
        background: '#f0f9ff', 
        borderRadius: '0.5rem', 
        marginBottom: '1rem',
        fontSize: '0.875rem',
        color: '#1e40af',
        border: '2px solid #3b82f6'
      }}>
        <strong>🔍 Debug Info:</strong><br/>
        Estabelecimento ID: {estabelecimento._id}<br/>
        Taxa Padrão: R$ {taxaPadrao}<br/>
        Bairros no estado: {taxasPorBairro.length}<br/>
        Bairros no banco: {estabelecimento?.configuracoes?.taxasPorBairro?.length || 0}<br/>
        Configurações existe: {estabelecimento?.configuracoes ? 'Sim' : 'Não'}<br/>
        TaxasPorBairro é array: {Array.isArray(estabelecimento?.configuracoes?.taxasPorBairro) ? 'Sim' : 'Não'}
      </div>

      {/* Seção: Taxas e Valores */}
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: '#ffffff',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb'
      }}>
        <h4 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: '600' }}>
          Taxas e Valores
        </h4>

        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          background: taxaEntregaAtiva ? '#d4edda' : '#fff3cd',
          borderRadius: '0.5rem',
          border: `2px solid ${taxaEntregaAtiva ? '#28a745' : '#f59e0b'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontWeight: 'bold', marginBottom: '0.25rem', display: 'block', fontSize: '1.05rem' }}>
                Taxa de entrega (liga/desliga)
              </label>
              <p style={{ margin: 0, fontSize: '0.95rem', color: taxaEntregaAtiva ? '#155724' : '#7c2d12' }}>
                {taxaEntregaAtiva
                  ? '✅ ATIVA: novos pedidos delivery irão cobrar taxa normalmente'
                  : '🎁 DESATIVADA: novos pedidos delivery sairão como Entrega Grátis (taxa = 0)'}
              </p>
              <small style={{ display: 'block', marginTop: '0.35rem', color: '#6c757d' }}>
                Importante: não altera pedidos antigos já vendidos.
              </small>
            </div>

            <button
              type="button"
              onClick={() => {
                const novoValor = !taxaEntregaAtiva;
                const ok = window.confirm(
                  novoValor
                    ? 'Ativar cobrança de taxa de entrega? (vale apenas para pedidos novos)'
                    : 'Desativar cobrança de taxa de entrega? (Entrega grátis, vale apenas para pedidos novos)'
                );
                if (!ok) return;
                toggleTaxaEntregaMutation.mutate(novoValor);
              }}
              disabled={toggleTaxaEntregaMutation.isLoading}
              style={{
                padding: '0.75rem 1.5rem',
                background: taxaEntregaAtiva ? '#dc3545' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                cursor: toggleTaxaEntregaMutation.isLoading ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                whiteSpace: 'nowrap',
                opacity: toggleTaxaEntregaMutation.isLoading ? 0.7 : 1
              }}
              title={taxaEntregaAtiva ? 'Desativar taxa de entrega (Entrega grátis)' : 'Ativar taxa de entrega'}
            >
              <DollarSign size={18} />
              {taxaEntregaAtiva ? 'DESLIGAR' : 'LIGAR'} TAXA
            </button>
          </div>
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Taxa de Entrega (R$)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={taxaPadrao}
              onChange={(e) => setTaxaPadrao(parseFloat(e.target.value) || 0)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #cbd5e1',
                borderRadius: '0.5rem',
                fontSize: '1rem'
              }}
            />
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#64748b' }}>
              Taxa padrão quando o bairro não estiver na lista
            </small>
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Valor Mínimo do Pedido (R$)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={estabelecimento?.configuracoes?.valorMinimoPedido || 0}
              onChange={async (e) => {
                const valor = parseFloat(e.target.value) || 0;
                try {
                  const configuracoesAtuais = estabelecimento?.configuracoes || {};
                  await api.put(`/api/estabelecimentos/${estabelecimento._id}`, {
                    configuracoes: {
                      ...configuracoesAtuais,
                      valorMinimoPedido: valor
                    }
                  });
                  queryClient.invalidateQueries('estabelecimento-config');
                  toast.success('Valor mínimo atualizado!');
                } catch (error) {
                  toast.error('Erro ao atualizar valor mínimo');
                }
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #cbd5e1',
                borderRadius: '0.5rem',
                fontSize: '1rem'
              }}
            />
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#64748b' }}>
              Valor mínimo para aceitar pedidos
            </small>
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Tempo Médio de Entrega (minutos)
            </label>
            <input
              type="number"
              min="0"
              value={estabelecimento?.configuracoes?.tempoMedioPreparo || 30}
              onChange={async (e) => {
                const tempo = parseInt(e.target.value) || 30;
                try {
                  const configuracoesAtuais = estabelecimento?.configuracoes || {};
                  await api.put(`/api/estabelecimentos/${estabelecimento._id}`, {
                    configuracoes: {
                      ...configuracoesAtuais,
                      tempoMedioPreparo: tempo
                    }
                  });
                  queryClient.invalidateQueries('estabelecimento-config');
                  toast.success('Tempo médio atualizado!');
                } catch (error) {
                  toast.error('Erro ao atualizar tempo médio');
                }
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #cbd5e1',
                borderRadius: '0.5rem',
                fontSize: '1rem'
              }}
            />
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#64748b' }}>
              Tempo médio de preparo e entrega
            </small>
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Horário da Marmita (início)
            </label>
            <input
              type="time"
              value={marmitaHorarioInicio}
              onChange={(e) => setMarmitaHorarioInicio(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #cbd5e1',
                borderRadius: '0.5rem',
                fontSize: '1rem'
              }}
            />
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#64748b' }}>
              Ex: 10:00
            </small>
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Horário da Marmita (fim)
            </label>
            <input
              type="time"
              value={marmitaHorarioFim}
              onChange={(e) => setMarmitaHorarioFim(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #cbd5e1',
                borderRadius: '0.5rem',
                fontSize: '1rem'
              }}
            />
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#64748b' }}>
              Ex: 14:30
            </small>
          </div>

          <div className="form-group">
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Intervalo (minutos)
            </label>
            <input
              type="number"
              min="5"
              step="5"
              value={marmitaIntervaloMin}
              onChange={(e) => setMarmitaIntervaloMin(parseInt(e.target.value, 10) || 30)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #cbd5e1',
                borderRadius: '0.5rem',
                fontSize: '1rem'
              }}
            />
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#64748b' }}>
              Ex: 30 (de 30 em 30 minutos)
            </small>
          </div>
        </div>
      </div>

      {/* Taxa Padrão */}
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: '#f0f9ff',
        borderRadius: '0.5rem',
        border: '2px solid #bae6fd'
      }}>
        <h4 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <DollarSign size={20} />
          Taxa Padrão (quando bairro não encontrado)
        </h4>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Taxa Padrão (R$)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={taxaPadrao}
              onChange={(e) => setTaxaPadrao(parseFloat(e.target.value) || 0)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #cbd5e1',
                borderRadius: '0.5rem',
                fontSize: '1rem'
              }}
            />
            <small style={{ display: 'block', marginTop: '0.5rem', color: '#64748b' }}>
              Esta taxa será usada quando o bairro do cliente não estiver na lista abaixo
            </small>
          </div>
          <button
            type="button"
            onClick={handleSalvarTaxaPadrao}
            disabled={saveMutation.isLoading}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              whiteSpace: 'nowrap'
            }}
          >
            <Save size={18} />
            Salvar Taxa Padrão
          </button>
        </div>
      </div>

      {/* Adicionar Novo Bairro */}
      <div style={{
        marginBottom: '2rem',
        padding: '1.5rem',
        background: '#f9fafb',
        borderRadius: '0.5rem',
        border: '1px solid #e5e7eb'
      }}>
        <h4 style={{ marginBottom: '1rem' }}>➕ Adicionar Novo Bairro</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '1rem', alignItems: 'flex-end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Nome do Bairro
            </label>
            <input
              type="text"
              value={novoBairro.bairro}
              onChange={(e) => setNovoBairro({ ...novoBairro, bairro: e.target.value })}
              placeholder="Ex: Centro, Jardim América..."
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #cbd5e1',
                borderRadius: '0.5rem',
                fontSize: '1rem'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  adicionarBairro();
                }
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>
              Taxa (R$)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={novoBairro.taxa}
              onChange={(e) => setNovoBairro({ ...novoBairro, taxa: parseFloat(e.target.value) || 0 })}
              placeholder="0.00"
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '2px solid #cbd5e1',
                borderRadius: '0.5rem',
                fontSize: '1rem'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  adicionarBairro();
                }
              }}
            />
          </div>
          <button
            type="button"
            onClick={adicionarBairro}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: '600',
              whiteSpace: 'nowrap'
            }}
          >
            Adicionar
          </button>
        </div>
      </div>

      {/* Busca */}
      {taxasPorBairro.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="🔍 Buscar bairro..."
            style={{
              width: '100%',
              padding: '0.75rem',
              border: '2px solid #cbd5e1',
              borderRadius: '0.5rem',
              fontSize: '1rem'
            }}
          />
        </div>
      )}

      {/* Lista de Bairros */}
      <div style={{
        marginBottom: '2rem',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        overflow: 'hidden'
      }}>
        {!estabelecimento?.configuracoes ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Carregando configurações...</p>
          </div>
        ) : taxasPorBairro.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
            <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Nenhum bairro cadastrado</p>
            <p>Adicione bairros acima para começar</p>
            <p style={{ fontSize: '0.875rem', marginTop: '1rem', color: '#ef4444' }}>
              Debug: taxasPorBairro.length = {taxasPorBairro.length}
            </p>
          </div>
        ) : bairrosOrdenados.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
            <p>Nenhum bairro encontrado com "{busca}"</p>
            <p style={{ fontSize: '0.875rem', marginTop: '1rem' }}>
              Total de bairros: {taxasPorBairro.length} | Filtrados: {bairrosFiltrados.length}
            </p>
          </div>
        ) : (
          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f9fafb', position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                    Bairro
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>
                    Taxa (R$)
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600', width: '150px' }}>
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {bairrosOrdenados.map((taxa, index) => {
                  const indexOriginal = taxasPorBairro.findIndex(t => 
                    t.bairro === taxa.bairro && t.taxa === taxa.taxa
                  );
                  const estaEditando = bairroEditando === indexOriginal;
                  
                  return (
                    <tr key={indexOriginal} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '1rem' }}>
                        {estaEditando ? (
                          <input
                            type="text"
                            value={taxa.bairro}
                            onChange={(e) => atualizarBairro(indexOriginal, 'bairro', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '2px solid #3b82f6',
                              borderRadius: '0.25rem',
                              fontSize: '0.95rem'
                            }}
                            autoFocus
                          />
                        ) : (
                          <span style={{ fontWeight: '500' }}>{taxa.bairro}</span>
                        )}
                      </td>
                      <td style={{ padding: '1rem' }}>
                        {estaEditando ? (
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={taxa.taxa}
                            onChange={(e) => atualizarBairro(indexOriginal, 'taxa', e.target.value)}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              border: '2px solid #3b82f6',
                              borderRadius: '0.25rem',
                              fontSize: '0.95rem'
                            }}
                          />
                        ) : (
                          <span style={{ color: '#059669', fontWeight: '600' }}>
                            R$ {taxa.taxa.toFixed(2).replace('.', ',')}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        {estaEditando ? (
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button
                              type="button"
                              onClick={() => salvarEdicao(indexOriginal)}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                              }}
                            >
                              ✓ Salvar
                            </button>
                            <button
                              type="button"
                              onClick={cancelarEdicao}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#6b7280',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                              }}
                            >
                              ✕ Cancelar
                            </button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <button
                              type="button"
                              onClick={() => iniciarEdicao(indexOriginal)}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                              }}
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => removerBairro(indexOriginal)}
                              style={{
                                padding: '0.5rem 1rem',
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                              }}
                            >
                              Remover
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Estatísticas */}
      {taxasPorBairro.length > 0 && (
        <div style={{
          padding: '1rem',
          background: '#f0f9ff',
          borderRadius: '0.5rem',
          marginBottom: '1.5rem',
          display: 'flex',
          gap: '2rem',
          justifyContent: 'center'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3b82f6' }}>
              {taxasPorBairro.length}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Bairros Cadastrados</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>
              R$ {(taxasPorBairro.reduce((acc, t) => acc + t.taxa, 0) / taxasPorBairro.length).toFixed(2).replace('.', ',')}
            </div>
            <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Taxa Média</div>
          </div>
        </div>
      )}

      {/* Botão Salvar Tudo */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
        <button
          type="button"
          onClick={handleSalvarTudo}
          disabled={saveMutation.isLoading || !estabelecimento?._id}
          style={{
            padding: '0.75rem 2rem',
            background: saveMutation.isLoading ? '#9ca3af' : 'var(--primary)',
            color: 'white',
            border: 'none',
            borderRadius: '0.5rem',
            cursor: saveMutation.isLoading ? 'not-allowed' : 'pointer',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            fontSize: '1rem'
          }}
        >
          <Save size={20} />
          {saveMutation.isLoading ? 'Salvando...' : 'Salvar Todas as Alterações'}
        </button>
      </div>
    </div>
  );
};

const ConfigAreas = ({ estabelecimento }) => {
  return (
    <div className="config-section">
      <h3>Áreas de Entrega</h3>
      <p>Configure as áreas onde você realiza entregas</p>
      <div className="areas-lista">
        {/* Implementar lista de áreas */}
        <p className="text-muted">Funcionalidade em desenvolvimento</p>
      </div>
    </div>
  );
};

const ConfigHorarios = ({ estabelecimento }) => {
  const queryClient = useQueryClient();
  
  // Estado inicial com valores padrão
  const horariosPadrao = {
    segunda: { aberto: true, inicio: '08:00', fim: '22:00' },
    terca: { aberto: true, inicio: '08:00', fim: '22:00' },
    quarta: { aberto: true, inicio: '08:00', fim: '22:00' },
    quinta: { aberto: true, inicio: '08:00', fim: '22:00' },
    sexta: { aberto: true, inicio: '08:00', fim: '22:00' },
    sabado: { aberto: true, inicio: '08:00', fim: '22:00' },
    domingo: { aberto: false, inicio: '08:00', fim: '22:00' }
  };

  // Carregar horários do estabelecimento ou usar padrão
  const [horarios, setHorarios] = useState(() => {
    if (estabelecimento?.configuracoes?.horarioFuncionamento) {
      return estabelecimento.configuracoes.horarioFuncionamento;
    }
    return horariosPadrao;
  });

  // Atualizar quando estabelecimento mudar
  useEffect(() => {
    if (estabelecimento?.configuracoes?.horarioFuncionamento) {
      setHorarios(estabelecimento.configuracoes.horarioFuncionamento);
    }
  }, [estabelecimento]);

  // Mutation para salvar horários
  const saveMutation = useMutation(
    (data) => {
      if (!estabelecimento?._id) {
        throw new Error('Estabelecimento não encontrado');
      }
      
      // Preservar todas as configurações existentes
      const configuracoesAtuais = estabelecimento.configuracoes || {};
      
      return api.put(`/api/estabelecimentos/${estabelecimento._id}`, {
        configuracoes: {
          ...configuracoesAtuais,
          horarioFuncionamento: data
        }
      });
    },
    {
      onSuccess: () => {
        toast.success('Horários de funcionamento salvos!');
        queryClient.invalidateQueries('estabelecimento-config');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao salvar horários');
      }
    }
  );

  const handleSalvar = () => {
    if (!estabelecimento?._id) {
      toast.error('Estabelecimento não encontrado');
      return;
    }
    saveMutation.mutate(horarios);
  };

  // Nomes dos dias em português
  const nomesDias = {
    segunda: 'Segunda-feira',
    terca: 'Terça-feira',
    quarta: 'Quarta-feira',
    quinta: 'Quinta-feira',
    sexta: 'Sexta-feira',
    sabado: 'Sábado',
    domingo: 'Domingo'
  };

  return (
    <div className="config-section">
      <h3>Horários de Funcionamento</h3>
      <p className="config-description">
        Configure os horários de funcionamento para cada dia da semana
      </p>
      
      <div className="horarios-lista">
        {Object.entries(horarios).map(([dia, horario]) => (
          <div key={dia} className="horario-item">
            <div className="horario-dia">
              <input
                type="checkbox"
                checked={horario.aberto || false}
                onChange={(e) => setHorarios({
                  ...horarios,
                  [dia]: { 
                    ...horario, 
                    aberto: e.target.checked,
                    inicio: horario.inicio || '08:00',
                    fim: horario.fim || '22:00'
                  }
                })}
              />
              <label>{nomesDias[dia] || dia.charAt(0).toUpperCase() + dia.slice(1)}</label>
            </div>
            {horario.aberto && (
              <div className="horario-tempo">
                <input
                  type="time"
                  value={horario.inicio || '08:00'}
                  onChange={(e) => setHorarios({
                    ...horarios,
                    [dia]: { ...horario, inicio: e.target.value }
                  })}
                />
                <span>até</span>
                <input
                  type="time"
                  value={horario.fim || '22:00'}
                  onChange={(e) => setHorarios({
                    ...horarios,
                    [dia]: { ...horario, fim: e.target.value }
                  })}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      
      <button 
        className="btn btn-primary" 
        style={{ marginTop: '1.5rem' }}
        onClick={handleSalvar}
        disabled={saveMutation.isLoading || !estabelecimento?._id}
      >
        <Save size={20} />
        {saveMutation.isLoading ? 'Salvando...' : 'Salvar Horários'}
      </button>
    </div>
  );
};

const ConfigWhatsApp = ({ estabelecimento }) => {
  const [formData, setFormData] = useState({
    useCloudAPI: false,
    accessToken: '',
    phoneNumberId: '',
    businessAccountId: '',
    verifyToken: ''
  });
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  // Buscar configuração atual
  const { data: configData, refetch: refetchConfig } = useQuery(
    'whatsapp-cloud-config',
    async () => {
      const response = await api.get('/api/whatsapp/cloud/config');
      return response.data.data;
    },
    {
      onSuccess: (data) => {
        setFormData({
          useCloudAPI: data.useCloudAPI || false,
          accessToken: data.accessToken || '',
          phoneNumberId: data.phoneNumberId || '',
          businessAccountId: data.businessAccountId || '',
          verifyToken: data.verifyToken || ''
        });
      },
      onError: (error) => {
        console.error('Erro ao buscar configuração WhatsApp:', error);
      }
    }
  );

  // Buscar status do número
  const { data: statusData, refetch: refetchStatus } = useQuery(
    'whatsapp-cloud-status',
    async () => {
      const response = await api.get('/api/whatsapp/cloud/status');
      return response.data.data;
    },
    {
      enabled: formData.useCloudAPI,
      refetchInterval: 30000, // Atualizar a cada 30 segundos
      onError: (error) => {
        console.error('Erro ao buscar status WhatsApp:', error);
      }
    }
  );

  const saveMutation = useMutation(
    (data) => api.put('/api/whatsapp/cloud/config', data),
    {
      onSuccess: () => {
        toast.success('Configuração da API oficial salva!');
        refetchConfig();
        refetchStatus();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao salvar configuração');
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (formData.useCloudAPI) {
      if (!formData.accessToken || !formData.phoneNumberId) {
        toast.error('Access Token e Phone Number ID são obrigatórios');
        return;
      }
    }

    setLoading(true);
    saveMutation.mutate(formData, {
      onSettled: () => {
        setLoading(false);
      }
    });
  };

  return (
    <div className="config-section">
      <h3>📱 Configuração do WhatsApp Business</h3>
      <p className="config-description">
        Configure a API oficial do WhatsApp Business para maior estabilidade e escalabilidade.
        Você gerencia os custos através da sua conta Meta Business.
      </p>

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.useCloudAPI}
              onChange={(e) => setFormData({ ...formData, useCloudAPI: e.target.checked })}
            />
            <span style={{ fontWeight: '500' }}>Usar API Oficial do WhatsApp Business (recomendado)</span>
          </label>
        </div>

        {formData.useCloudAPI && (
          <>
            <div className="form-group">
              <label>Access Token *</label>
              <input
                type="password"
                className="input"
                value={formData.accessToken}
                onChange={(e) => setFormData({ ...formData, accessToken: e.target.value })}
                placeholder="EAAxxxxxxxxxxxxx"
                required={formData.useCloudAPI}
              />
              <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
                Token de acesso da sua conta Meta Business.
                <a href="https://developers.facebook.com/docs/whatsapp/cloud-api/get-started" target="_blank" rel="noopener noreferrer" style={{ marginLeft: '0.5rem', color: 'var(--primary)' }}> Saiba mais</a>
              </small>
            </div>

            <div className="form-group">
              <label>Phone Number ID *</label>
              <input
                type="text"
                className="input"
                value={formData.phoneNumberId}
                onChange={(e) => setFormData({ ...formData, phoneNumberId: e.target.value })}
                placeholder="123456789012345"
                required={formData.useCloudAPI}
              />
              <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
                ID do número de telefone na Meta Business.
              </small>
            </div>

            <div className="form-group">
              <label>Business Account ID (opcional)</label>
              <input
                type="text"
                className="input"
                value={formData.businessAccountId}
                onChange={(e) => setFormData({ ...formData, businessAccountId: e.target.value })}
                placeholder="123456789012345"
              />
              <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
                ID da sua conta de negócios no Meta Business.
              </small>
            </div>

            <div className="form-group">
              <label>Verify Token (para webhook)</label>
              <input
                type="text"
                className="input"
                value={formData.verifyToken}
                onChange={(e) => setFormData({ ...formData, verifyToken: e.target.value })}
                placeholder="meu_token_secreto"
              />
              <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
                Token para verificação do webhook (configure no Meta Business Manager).
              </small>
            </div>

            {statusData && (
              <div style={{ 
                marginBottom: '1.5rem', 
                padding: '1rem', 
                background: statusData.verified ? '#d4edda' : '#f8d7da',
                borderRadius: '0.5rem',
                border: `1px solid ${statusData.verified ? '#c3e6cb' : '#f5c6cb'}`
              }}>
                <h5 style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {statusData.verified ? (
                    <>
                      <CheckCircle size={20} color="#155724" />
                      <span>Número Verificado</span>
                    </>
                  ) : (
                    <>
                      <XCircle size={20} color="#721c24" />
                      <span>Número Não Verificado</span>
                    </>
                  )}
                </h5>
                {statusData.displayName && <p><strong>Nome:</strong> {statusData.displayName}</p>}
                {statusData.phoneNumber && <p><strong>Telefone:</strong> {statusData.phoneNumber}</p>}
                {statusData.error && <p style={{ color: '#721c24' }}><strong>Erro:</strong> {statusData.error}</p>}
              </div>
            )}

            {configData?.webhookUrl && (
              <div style={{ 
                marginBottom: '1.5rem', 
                padding: '1rem', 
                background: '#e7f3ff',
                borderRadius: '0.5rem',
                border: '1px solid #b3d9ff'
              }}>
                <p style={{ marginBottom: '0.5rem', fontWeight: '500' }}>🔗 URL do Webhook:</p>
                <code style={{ 
                  display: 'block', 
                  padding: '0.5rem', 
                  background: '#fff',
                  borderRadius: '0.25rem',
                  wordBreak: 'break-all',
                  fontSize: '0.9rem'
                }}>
                  {configData.webhookUrl}
                </code>
                <small style={{ color: '#666', fontSize: '0.85rem', display: 'block', marginTop: '0.5rem' }}>
                  Configure esta URL no Meta Business Manager → Configurações → Webhooks
                </small>
              </div>
            )}
          </>
        )}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading || saveMutation.isLoading}
        >
          <Save size={18} />
          {loading || saveMutation.isLoading ? 'Salvando...' : 'Salvar Configuração'}
        </button>
      </form>

      <div style={{ marginTop: '2rem', padding: '1rem', background: '#fff3cd', borderRadius: '0.5rem', border: '1px solid #ffc107' }}>
        <h5 style={{ marginBottom: '0.5rem' }}>ℹ️ Como obter as credenciais:</h5>
        <ol style={{ marginLeft: '1.5rem', fontSize: '0.9rem', lineHeight: '1.6' }}>
          <li>Crie uma conta no <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Meta Business Manager</a></li>
          <li>Configure um número de telefone para WhatsApp Business</li>
          <li>Obtenha o Access Token e Phone Number ID nas configurações</li>
          <li>Configure o webhook com a URL fornecida acima</li>
          <li>Você pagará os custos das mensagens, seus clientes apenas escaneiam o QR Code</li>
        </ol>
      </div>
    </div>
  );
};

const ConfigImpressora = ({ estabelecimento }) => {
  const [formData, setFormData] = useState({
    ativa: estabelecimento?.impressora?.ativa || false,
    tipo: estabelecimento?.impressora?.tipo || 'EPSON',
    interface: estabelecimento?.impressora?.interface || 'tcp',
    endereco: estabelecimento?.impressora?.endereco || '',
    porta: estabelecimento?.impressora?.porta || 9100,
    largura: estabelecimento?.impressora?.largura || 58,
    cortarAutomatico: estabelecimento?.impressora?.cortarAutomatico !== false,
    abrirGaveta: estabelecimento?.impressora?.abrirGaveta || false
  });

  const [testando, setTestando] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState(null);
  const queryClient = useQueryClient();

  const saveMutation = useMutation(
    async (data) => {
      const response = await api.put(`/api/estabelecimentos/${estabelecimento._id}/impressora`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Configurações da impressora salvas!');
        queryClient.invalidateQueries('estabelecimento-config');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erro ao salvar configurações');
      }
    }
  );

  const testarMutation = useMutation(
    async (dados) => {
      const response = await api.post(`/api/estabelecimentos/${estabelecimento._id}/impressora/testar`, dados || formData);
      return response.data;
    },
    {
      onMutate: () => {
        setTestando(true);
        setResultadoTeste(null);
      },
      onSuccess: (data) => {
        setTestando(false);
        const mensagem = data.message || 'Impressora testada com sucesso!';
        setResultadoTeste({ sucesso: true, mensagem: mensagem });
        
        console.log('📋 Dados recebidos:', data);
        
        if (data.arquivoSalvo && data.conteudoImpressao) {
          // Se for USB local, tentar imprimir no navegador
          if (data.tipo === 'usb_local') {
            console.log('🖨️ Tentando imprimir conteúdo USB local...');
            setTimeout(() => {
              imprimirNoNavegador(data.conteudoImpressao);
            }, 500);
            toast.success('✅ Abrindo janela de impressão...', { duration: 3000 });
          } else {
            toast.success('✅ Arquivo salvo! ' + mensagem, { duration: 6000 });
          }
        } else {
          toast.success('Impressora testada com sucesso!');
        }
      },
      onError: (error) => {
        setTestando(false);
        const mensagemErro = error.response?.data?.message || error.message || 'Erro ao testar impressora';
        setResultadoTeste({ 
          sucesso: false, 
          mensagem: mensagemErro
        });
        toast.error(mensagemErro);
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validação: endereço obrigatório apenas para TCP/IP
    if (formData.interface === 'tcp' && (!formData.endereco || String(formData.endereco).trim() === '')) {
      toast.error('O endereço/IP da impressora é obrigatório para impressora TCP/IP');
      return;
    }
    
    // Para USB, endereço é opcional (pode estar vazio)
    saveMutation.mutate(formData);
  };

  // Função para imprimir no navegador (USB local)
  const imprimirNoNavegador = (conteudo) => {
    try {
      console.log('🖨️ Iniciando impressão no navegador...', conteudo);
      
      // Criar uma nova janela com o conteúdo formatado para impressão
      const janelaImpressao = window.open('', '_blank', 'width=400,height=600');
      
      if (!janelaImpressao || janelaImpressao.closed || typeof janelaImpressao.closed === 'undefined') {
        console.error('❌ Pop-up bloqueado!');
        toast.error('⚠️ Pop-up bloqueado! Por favor, permita pop-ups para este site e tente novamente.', { duration: 6000 });
        
        // Alternativa: criar elemento oculto e imprimir
        const elementoImpressao = document.createElement('div');
        elementoImpressao.style.position = 'fixed';
        elementoImpressao.style.left = '-9999px';
        elementoImpressao.style.top = '-9999px';
        elementoImpressao.style.fontFamily = "'Courier New', monospace";
        elementoImpressao.style.fontSize = '12px';
        elementoImpressao.style.whiteSpace = 'pre-wrap';
        elementoImpressao.textContent = conteudo;
        document.body.appendChild(elementoImpressao);
        
        setTimeout(() => {
          window.print();
          document.body.removeChild(elementoImpressao);
        }, 500);
        return;
      }

      // Formatar o conteúdo para impressão térmica (fonte monoespaçada, pequena)
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Impressão - Teste Impressora TANCA TP-550</title>
          <meta charset="utf-8">
          <style>
            @media print {
              @page {
                size: 80mm auto;
                margin: 0;
              }
              body {
                margin: 0;
                padding: 5mm;
                font-family: 'Courier New', monospace;
                font-size: 11px;
                line-height: 1.3;
                white-space: pre-wrap;
              }
            }
            body {
              font-family: 'Courier New', monospace;
              font-size: 11px;
              line-height: 1.3;
              white-space: pre-wrap;
              padding: 20px;
              max-width: 80mm;
              margin: 0 auto;
            }
            .no-print {
              display: none;
            }
          </style>
        </head>
        <body>
          <div class="no-print" style="text-align: center; padding: 20px; background: #f0f0f0; margin-bottom: 20px;">
            <h3>Teste de Impressora - TANCA TP-550</h3>
            <p>Selecione sua impressora e clique em Imprimir</p>
            <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; background: #007bff; color: white; border: none; border-radius: 5px; cursor: pointer;">🖨️ Imprimir Agora</button>
          </div>
          <pre style="font-family: 'Courier New', monospace; font-size: 11px; line-height: 1.3;">${conteudo.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</pre>
        </body>
        </html>
      `;
      
      janelaImpressao.document.open();
      janelaImpressao.document.write(htmlContent);
      janelaImpressao.document.close();
      
      console.log('✅ Janela de impressão criada');
      
      // Aguardar o conteúdo carregar e então focar na janela
      setTimeout(() => {
        janelaImpressao.focus();
        // Não chamar print() automaticamente - deixar o usuário clicar no botão
        console.log('✅ Janela pronta para impressão');
      }, 1000);
      
    } catch (error) {
      console.error('❌ Erro ao imprimir:', error);
      toast.error('Erro ao abrir janela de impressão: ' + error.message);
    }
  };

  const handleTestar = () => {
    // Validar campos obrigatórios antes de testar
    if (!formData.tipo || String(formData.tipo).trim() === '') {
      toast.error('Selecione o tipo de impressora primeiro');
      return;
    }
    if (!formData.interface || String(formData.interface).trim() === '') {
      toast.error('Selecione o tipo de interface primeiro');
      return;
    }
    
    // Para TCP/IP, endereço é obrigatório
    if (formData.interface === 'tcp' && (!formData.endereco || String(formData.endereco).trim() === '')) {
      toast.error('Preencha o endereço/IP da impressora primeiro');
      return;
    }
    
    // Garantir que todos os campos estão preenchidos e limpos
    const dadosTeste = {
      tipo: String(formData.tipo || 'EPSON').trim(),
      interface: String(formData.interface || 'tcp').trim(),
      endereco: String(formData.endereco || '').trim(),
      porta: parseInt(formData.porta) || 9100,
      largura: parseInt(formData.largura) || 58,
      cortarAutomatico: formData.cortarAutomatico !== false,
      abrirGaveta: formData.abrirGaveta || false
    };

    // Validação final: endereço obrigatório apenas para TCP/IP
    if (dadosTeste.interface === 'tcp' && (!dadosTeste.endereco || dadosTeste.endereco === '')) {
      toast.error('O endereço/IP da impressora é obrigatório para impressora TCP/IP');
      return;
    }

    console.log('📋 Enviando dados para teste:', dadosTeste);
    testarMutation.mutate(dadosTeste);
  };

  if (!estabelecimento) {
    return <div>Carregando...</div>;
  }

  return (
    <div className="config-section">
      <h3><Printer size={24} /> Configurações da Impressora Térmica</h3>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={formData.ativa}
              onChange={(e) => setFormData({ ...formData, ativa: e.target.checked })}
            />
            {' '}Ativar impressora térmica
          </label>
          <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280' }}>
            Quando ativada, as comandas serão impressas automaticamente
          </small>
        </div>

        {formData.ativa && (
          <>
            <div className="form-group">
              <label>Marca/Modelo da Impressora *</label>
              <select
                value={formData.tipo}
                onChange={(e) => setFormData({ ...formData, tipo: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid var(--gray-300)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem'
                }}
              >
                <option value="EPSON">Epson (TM-T20, TM-T82, TM-T88)</option>
                <option value="STAR">Star (TSP100, TSP650)</option>
                <option value="BEMATECH">Bematech (MP-4200, MP-2800)</option>
                <option value="ELGIN">Elgin (i9, i7)</option>
                <option value="DARUMA">Daruma (DR-800, DR-600)</option>
                <option value="TSP650">TSP650</option>
                <option value="TANCA">Tanca (TP-550, TP-80)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Tipo de Conexão *</label>
              <select
                value={formData.interface}
                onChange={(e) => setFormData({ ...formData, interface: e.target.value })}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid var(--gray-300)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem'
                }}
              >
                <option value="tcp">🌐 Rede (TCP/IP) - Wi-Fi ou Ethernet</option>
                <option value="usb">🔌 USB - Conectada diretamente ao computador</option>
                <option value="serial">📡 Serial (COM) - Porta serial</option>
              </select>
            </div>

            {formData.interface === 'tcp' && (
              <>
                <div className="form-group">
                  <label>Endereço IP da Impressora *</label>
                  <input
                    type="text"
                    value={formData.endereco}
                    onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                    placeholder="192.168.1.100"
                    required
                  />
                  <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280' }}>
                    Encontre o IP da impressora nas configurações dela ou no roteador
                  </small>
                </div>

                <div className="form-group">
                  <label>Porta *</label>
                  <input
                    type="number"
                    value={formData.porta}
                    onChange={(e) => setFormData({ ...formData, porta: parseInt(e.target.value) || 9100 })}
                    placeholder="9100"
                    required
                  />
                  <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280' }}>
                    Porta padrão: 9100 (geralmente não precisa mudar)
                  </small>
                </div>
              </>
            )}

            {formData.interface === 'usb' && (
              <div className="form-group">
                <label>Caminho USB (Opcional)</label>
                <input
                  type="text"
                  value={formData.endereco}
                  onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  placeholder="Deixe em branco - não é mais necessário"
                />
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#10b981' }}>
                  ✅ <strong>Não precisa mais configurar!</strong> A impressão USB funciona automaticamente quando o PDV ou Garçom estão abertos no PC do estabelecimento (igual iFood/K2).
                </small>
              </div>
            )}

            {formData.interface === 'serial' && (
              <div className="form-group">
                <label>Porta Serial *</label>
                <input
                  type="text"
                  value={formData.endereco}
                  onChange={(e) => setFormData({ ...formData, endereco: e.target.value })}
                  placeholder="COM3 (Windows) ou /dev/ttyUSB0 (Linux)"
                  required
                />
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280' }}>
                  Windows: COM1, COM2, COM3, etc. | Linux: /dev/ttyUSB0, /dev/ttyUSB1, etc.
                </small>
              </div>
            )}

            <div className="form-group">
              <label>Largura do Papel *</label>
              <select
                value={formData.largura}
                onChange={(e) => setFormData({ ...formData, largura: parseInt(e.target.value) })}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '2px solid var(--gray-300)',
                  borderRadius: '0.5rem',
                  fontSize: '1rem'
                }}
              >
                <option value={58}>58mm (padrão)</option>
                <option value={80}>80mm (largura)</option>
              </select>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.cortarAutomatico}
                  onChange={(e) => setFormData({ ...formData, cortarAutomatico: e.target.checked })}
                />
                {' '}Cortar papel automaticamente após impressão
              </label>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={formData.abrirGaveta}
                  onChange={(e) => setFormData({ ...formData, abrirGaveta: e.target.checked })}
                />
                {' '}Abrir gaveta de dinheiro após impressão (se suportado)
              </label>
            </div>

            {/* Botão de Teste */}
            <div style={{
              padding: '1.5rem',
              background: '#f3f4f6',
              borderRadius: '0.5rem',
              marginBottom: '1.5rem',
              border: '2px dashed #d1d5db'
            }}>
              <h4 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <TestTube size={20} /> Testar Impressora
              </h4>
              <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                Clique no botão abaixo para enviar uma página de teste para a impressora
              </p>
              <button
                type="button"
                onClick={handleTestar}
                disabled={testando || !formData.endereco}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: testando ? '#9ca3af' : 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  cursor: testando ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {testando ? (
                  <>
                    <RefreshCw size={18} className="spinning" />
                    Testando...
                  </>
                ) : (
                  <>
                    <TestTube size={18} />
                    Enviar Página de Teste
                  </>
                )}
              </button>

              {resultadoTeste && (
                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: resultadoTeste.sucesso ? '#d1fae5' : '#fee2e2',
                  border: `1px solid ${resultadoTeste.sucesso ? '#10b981' : '#ef4444'}`,
                  borderRadius: '0.5rem',
                  color: resultadoTeste.sucesso ? '#065f46' : '#991b1b'
                }}>
                  {resultadoTeste.sucesso ? (
                    <><CheckCircle size={20} style={{ display: 'inline', marginRight: '0.5rem' }} /></>
                  ) : (
                    <><XCircle size={20} style={{ display: 'inline', marginRight: '0.5rem' }} /></>
                  )}
                  {resultadoTeste.mensagem}
                </div>
              )}
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <button
            type="submit"
            disabled={saveMutation.isLoading}
            style={{
              padding: '0.75rem 2rem',
              background: 'var(--primary)',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Save size={18} />
            {saveMutation.isLoading ? 'Salvando...' : 'Salvar Configurações'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Configuracoes;

